import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../App";
import { Globe, MapPin, FileText, Shield, ArrowRight, CheckCircle, Eye, Star, ExternalLink } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSampleModal, setShowSampleModal] = useState(false);

  const features = [
    {
      icon: Shield,
      title: "Trusted Data",
      description: "Information informed by relocation research and real expat experiences."
    },
    {
      icon: Globe,
      title: "Personalized Matching",
      description: "Country options aligned with your lifestyle priorities, budget, and relocation goals."
    },
    {
      icon: MapPin,
      title: "Global Coverage",
      description: "Insights across 100+ countries, including common visa pathways and relocation considerations."
    },
    {
      icon: FileText,
      title: "Personalized Relocation Decision Guide",
      description: "Practical relocation insights covering visas, banking basics, housing considerations, healthcare, and more."
    }
  ];

  const benefits = [
    "Compare countries side by side",
    "Understand visa pathways early",
    "See realistic cost expectations",
    "Skip months of scattered research"
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-primary" data-testid="logo">
            ExpatMatch
          </h1>
          <nav className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/forum")}
              className="hidden sm:flex"
              data-testid="forum-nav-btn"
            >
              Community
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/faq")}
              className="hidden sm:flex"
              data-testid="faq-nav-btn"
            >
              Help
            </Button>
            {user ? (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/dashboard")}
                  data-testid="dashboard-nav-btn"
                >
                  Dashboard
                </Button>
                <Button 
                  onClick={() => navigate("/questionnaire")}
                  className="btn-animated bg-secondary hover:bg-secondary/90"
                  data-testid="get-started-nav-btn"
                >
                  Find Your Country
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/auth")}
                  data-testid="login-nav-btn"
                >
                  Log In
                </Button>
                <Button 
                  onClick={() => navigate("/auth?mode=register")}
                  className="btn-animated bg-secondary hover:bg-secondary/90"
                  data-testid="signup-nav-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/hero-relocation.jpg')"
          }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Floating "preview" button positioned over the passport (desktop only) */}
        <button
          type="button"
          onClick={() => setShowSampleModal(true)}
          className="absolute z-20 hidden lg:flex items-center gap-3 left-[3%] bottom-[10%] group cursor-pointer"
          data-testid="hero-passport-preview-btn"
          aria-label="Preview a sample relocation guide"
        >
          <span className="relative flex">
            <span className="absolute inline-flex h-full w-full rounded-full bg-secondary/60 opacity-75 animate-ping" />
            <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-2xl ring-4 ring-white/40 group-hover:scale-110 transition-transform">
              <Eye className="h-6 w-6 text-primary" />
            </span>
          </span>
          <span className="bg-primary/95 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all whitespace-nowrap">
            Tap to preview a sample Guide
          </span>
        </button>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 slide-in" data-testid="hero-title">
              Discover Your Perfect
              <span className="text-secondary block mt-2">New Home Abroad</span>
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-xl slide-in stagger-1" data-testid="hero-subtitle">
              Answer a few questions about your lifestyle and discover countries aligned with your goals and priorities. Then receive your Personalized Relocation Decision Guide — just $20 for one country, $30 for two, or $35 for all three top matches.
            </p>
            <div className="flex flex-wrap gap-4 slide-in stagger-2">
              <Button 
                size="lg"
                onClick={() => navigate("/sample-report/Honduras")}
                className="btn-animated bg-secondary hover:bg-secondary/90 text-lg px-8 py-6 rounded-full"
                data-testid="sample-report-btn"
              >
                <Eye className="mr-2 h-5 w-5" />
                Preview the Guide
              </Button>
              <Button 
                size="lg"
                onClick={() => navigate(user ? "/questionnaire" : "/auth?mode=register")}
                className="btn-animated bg-accent hover:bg-accent/90 text-lg px-8 py-6 rounded-full text-white"
                data-testid="hero-cta-btn"
              >
                Start Your Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
          
          <div className="lg:col-span-5 hidden lg:block">
            <div className="bg-card/90 backdrop-blur-sm rounded-2xl p-8 border border-primary/10 slide-in stagger-3">
              <h3 className="text-xl font-semibold text-primary mb-4">How We Help You Decide</h3>
              <ul className="space-y-3">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3 text-foreground/80">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-testid="features-title">
              How ExpatMatch Works
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Moving abroad isn't just about where you want to go — it's about how your priorities fit together. Our Guide clarifies the tradeoffs around cost, visas, lifestyle, and opportunity, then surfaces countries aligned with what matters most to you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <div 
                key={feature.title || `feature-${i}`}
                className="bg-card p-8 rounded-xl border border-primary/10 card-hover"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-primary mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Report Preview Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 rounded-full text-secondary text-sm font-medium mb-4">
              <Star className="h-4 w-4" />
              Preview Our Guides
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4" data-testid="sample-section-title">
              What You'll Find in Your Personal Guide
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our personalized Guides provide the key insights you need to make a more informed relocation decision.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Report Content Preview */}
            <div className="bg-card rounded-2xl border border-primary/10 p-8 space-y-6">
              <h3 className="text-xl font-semibold text-primary">What's Included in Every Personal Guide:</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  "Executive Summary",
                  "Visa & Immigration Guide",
                  "Cost of Living Breakdown",
                  "Healthcare System Overview",
                  "Real Estate Laws & Prices",
                  "Banking & Finance Tips",
                  "Tax Information for Expats",
                  "Internet & Remote Work",
                  "Daily Life & Culture",
                  "6-Month Relocation Timeline"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                    <span className="text-foreground/80 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-primary/10">
                <p className="text-sm text-muted-foreground mb-4">
                  Each Guide is built around your specific priorities, budget, lifestyle, and family situation.
                </p>
                <Button 
                  onClick={() => navigate("/sample-report/Honduras")}
                  className="btn-animated bg-primary hover:bg-primary/90 gap-2"
                  data-testid="view-sample-btn"
                >
                  <Eye className="h-4 w-4" />
                  View Sample Guide for Honduras
                </Button>
              </div>
            </div>
            
            {/* Stats Preview */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl border border-primary/10 p-8">
                <h3 className="text-lg font-semibold text-primary mb-2">Inside the Guide: Key Indicators</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  We break down essential indicators so you can evaluate each country with context — not just numbers.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Cost of Living Index", value: "62" },
                    { label: "Safety Index", value: "79" },
                    { label: "Healthcare Index", value: "80" },
                    { label: "Quality of Life", value: "76" }
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white/50 rounded-xl p-4 text-center">
                      <p className="font-mono text-2xl font-bold text-primary">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-accent/10 rounded-xl p-6 border border-accent/20">
                <p className="text-sm font-medium text-primary mb-2">Quick Facts Included:</p>
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0" />
                    Capital, currency, language, and time zone
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0" />
                    US Embassy contact information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full mt-2 flex-shrink-0" />
                    Key immigration offices and contacts
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-6" data-testid="cta-title">
            Ready to Take Your First Step Toward Moving Abroad?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Take the Assessment and receive your Personalized Relocation Guide.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate(user ? "/questionnaire" : "/auth?mode=register")}
            className="btn-animated bg-primary hover:bg-primary/90 text-lg px-10 py-6 rounded-full"
            data-testid="bottom-cta-btn"
          >
            Start the Assessment
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-primary mb-4">ExpatMatch</h3>
              <p className="text-sm text-muted-foreground">
                AI-powered country matching for Americans looking to relocate abroad.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-3">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><span onClick={() => navigate("/questionnaire")} className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">Find Your Country</span></li>
                <li><span onClick={() => navigate("/sample-report/Honduras")} className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">Sample Report</span></li>
                <li><span onClick={() => navigate("/forum")} className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">Community Forum</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-3">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><span onClick={() => navigate("/faq")} className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">FAQ</span></li>
                <li><span onClick={() => navigate("/contact")} className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">Contact Us</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><span className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">Privacy Policy</span></li>
                <li><span className="text-muted-foreground hover:text-primary cursor-pointer transition-colors">Terms of Service</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-primary/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground" data-testid="footer-company">
              © {new Date().getFullYear()} Get Expat Match, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Sample Guide Modal — triggered by the Eye button on the passport */}
      <Dialog open={showSampleModal} onOpenChange={setShowSampleModal}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[88vh] p-0 overflow-hidden flex flex-col"
          data-testid="hero-sample-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-primary/10 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle className="text-primary text-xl font-bold flex items-center gap-2">
                  <Eye className="h-5 w-5 text-secondary" />
                  Sample Relocation Decision Guide — Honduras
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  This is exactly the kind of personalized Guide you'll receive after taking the Assessment.
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowSampleModal(false);
                  navigate("/sample-report/Honduras");
                }}
                className="hidden sm:flex gap-2 flex-shrink-0"
                data-testid="hero-sample-modal-open-fullpage-btn"
              >
                Open full page
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-background">
            <iframe
              src="/sample-report/Honduras"
              title="Sample Relocation Decision Guide for Honduras"
              className="w-full h-full border-0"
              data-testid="hero-sample-modal-iframe"
            />
          </div>
          <div className="px-6 py-3 border-t border-primary/10 bg-card flex items-center justify-between gap-4 flex-shrink-0">
            <p className="text-xs text-muted-foreground hidden sm:block">
              Your Guide will be customized to your budget, family, and lifestyle.
            </p>
            <Button
              onClick={() => {
                setShowSampleModal(false);
                navigate(user ? "/questionnaire" : "/auth?mode=register");
              }}
              className="btn-animated bg-accent hover:bg-accent/90 text-white gap-2 ml-auto"
              data-testid="hero-sample-modal-start-btn"
            >
              Start My Assessment
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
